<template>
  <div class="EditProfileForm w-full">
    <div v-if="error.description" class="mb-32">
      <CardWrapper
        type-class="card--error"
        class="my-16"
        :is-dismissable="true"
        @close-card="closeErrorMessage"
      >
        <template #body>
          <h3 v-if="error.title">
            {{ error.title }}
          </h3>
          {{ error.description }}
        </template>
      </CardWrapper>
    </div>

    <div class="EditProfileForm__pic mx-auto mb-24">
      <div v-if="isLoading" class="EditProfileForm__pic-loader">
        <LoadingDots
          dots-bg-color-class="bg-brand-purple"
          :has-borders="true"
        />
      </div>

      <img
        v-if="profilePicturePlaceholder"
        class="EditProfileForm__pic-img"
        :src="profilePicturePlaceholder"
        alt="Profile picture placeholder"
      />
      <img
        v-else-if="user.avatar"
        class="EditProfileForm__pic-img"
        :src="fullProfileURL(user.avatar)"
        alt="Your profile picture"
      />
      <img
        v-else
        class="EditProfileForm__pic-img"
        src="/src/assets/images/alien_avatar_space_ufo_icon.png"
        alt="Profile picture placeholder"
      />
      <button class="EditProfileForm__edit-btn icon-pencil">
        <input
          ref="inputFileElement"
          class="EditProfileForm__edit-btn__input-file"
          type="file"
          :disabled="isLoading"
          @change="onFileSelected"
        />
      </button>
    </div>

    <form @submit.prevent="sendEditProfileForm">
      <div class="grid gap-0 md-gap-24 col-1 md-col-2 w-full mb-0">
        <InputField
          id="first_name"
          v-model="accountForm.firstName"
          type="text"
          left-icon-class="icon-users"
          right-icon-class="icon-close input__right-icon--hover"
          :label="$t('first_name')"
          :has-error="v$.firstName.$invalid"
          :has-right-reset-button="true"
          :required="false"
          @on-right-click="accountForm.firstName = ''"
        />

        <InputField
          id="last_name"
          v-model="accountForm.lastName"
          type="text"
          left-icon-class="icon-users"
          right-icon-class="icon-close input__right-icon--hover"
          :label="$t('last_name')"
          :has-error="!!v$.lastName.$errors.length"
          :has-right-reset-button="true"
          :required="false"
          @on-right-click="accountForm.lastName = ''"
        />
      </div>

      <InputField
        id="nickName"
        v-model="accountForm.nickName"
        type="text"
        left-icon-class="icon-users"
        right-icon-class="icon-close input__right-icon--hover"
        :label="'Nickname'"
        :has-error="!!v$.nickName.$errors.length"
        :has-right-reset-button="true"
        :required="false"
        @on-right-click="accountForm.nickName = ''"
      />

      <div class="mb-24">
        <SelectField
          id="accountFormLang"
          v-model="accountForm.language"
          :options="languages"
          value-prop-name="locale"
          :placeholder="$t('select_your_language')"
          icon-class-left="icon-lang"
          icon-class-right="icon-simple-arrow-down"
          :has-error="!!v$.language.$errors.length"
          @change="onLanguageChange"
        />
      </div>

      <InputField
        id="email"
        v-model="accountForm.email"
        type="text"
        :is-disabled="true"
        left-icon-class="icon-mail-envelope"
        :label="$t('email')"
        :has-right-reset-button="true"
        :required="false"
        @on-right-click="accountForm.email = ''"
      />

      <CheckboxField
        v-model:checked="accountForm.subscribed"
        class="mb-24"
        field-id="subscribed"
        :label="$t('i_want_to_register_to_the_newsletter')"
      />

      <button
        class="btn btn--purple"
        :class="{ 'btn--loading': isLoading }"
        :disabled="v$.$invalid || !userHasChangedFormData"
        type="submit"
      >
        <template v-if="isLoading">
          <LoadingDots />
        </template>
        <template v-else>
          {{ $t('save_changes') }}
        </template>
      </button>
    </form>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { CardWrapper, LoadingDots } from '@snowshoe-frontend/shared-components'
import {
  InputField,
  SelectField,
  CheckboxField
} from '@snowshoe-frontend/form-fields-components'
import useVuelidate from '@vuelidate/core'
import { dataURLtoFile } from '@/helpers/dataURLToFile.js'
import { schemaToValidators } from '@snowshoe-frontend/utils-component'
import profileSchema from '@/openapi-schemas/profile.json'
import { storeToRefs } from 'pinia'
import { useNotificationsStore } from '@/stores/notificationsStore'
import { useCommonStore } from '@/stores/commonStore'
import { useErrorsStore } from '@/stores/errorsStore'
import { useUserStore } from '@/stores/userStore'
import { useI18n } from 'vue-i18n'
import { useLanguages } from '@/composables/useLanguages'
import { useModalStore } from '@/stores/modalStore'
import CropperPopupComponent from '@/components/cropper/CropperPopupComponent.vue'

const emits = defineEmits(['scrollToTop', 'editFormSent'])

const { t } = useI18n()
const { changeLanguage } = useLanguages()
const { setNotification } = useNotificationsStore()
const { createModal } = useModalStore()
const commonStore = useCommonStore()
const errorStore = useErrorsStore()
const userStore = useUserStore()

const { user } = storeToRefs(userStore)
const { languages } = storeToRefs(commonStore)
const { error } = storeToRefs(errorStore)

const accountForm = reactive({
  firstName: '',
  lastName: '',
  email: '',
  language: '',
  nickName: '',
  subscribed: false
})

const originalFormData = reactive({
  firstName: '',
  lastName: '',
  email: '',
  language: '',
  nickName: '',
  subscribed: false
})

const isLoading = ref(false)
const selectedFile = ref(null)
const profilePicturePlaceholder = ref(null)
const imageproxiURL = import.meta.env.VITE_IMAGEPROXY_URL

const rules = {
  ...schemaToValidators(profileSchema)
}

const v$ = useVuelidate(rules, accountForm, { $autoDirty: true })

const userHasChangedFormData = computed(() => {
  return (
    JSON.stringify(accountForm) !== JSON.stringify(originalFormData) ||
    selectedFile.value
  )
})

function initializeFormData() {
  const { firstName, lastName, nickName, email, subscribed, language } =
    user.value

  const initialObject = {
    firstName,
    lastName,
    email,
    language,
    nickName: String(nickName),
    subscribed
  }

  Object.assign(accountForm, initialObject)
  Object.assign(originalFormData, initialObject)

  selectedFile.value = null
}

initializeFormData()

function scrollToTop() {
  document.getElementById('AppMainContainer').scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

async function sendEditProfileForm() {
  try {
    isLoading.value = true

    await userStore.editProfile({
      selectedFile: selectedFile.value,
      profilePayload: accountForm
    })
    await userStore.getUserData()
    emits('editFormSent', true)
    errorStore.resetErrorObject()
    scrollToTop()
    initializeFormData()
  } catch (error) {
    setNotification({
      header: t('account_common_error'),
      body: 'Whoops! It seems we have problems with changing your profile.',
      type: 'danger'
    })

    throw new Error(error)
  } finally {
    isLoading.value = false
  }
}

async function setCroppedImg(e) {
  const profilePicturePlaceholder = await createModal({
    component: CropperPopupComponent,
    props: {
      imgFile: e.target.result,
      saveText: t('save')
    }
  })

  if (profilePicturePlaceholder) {
    selectedFile.value = dataURLtoFile(
      profilePicturePlaceholder,
      'profile_picture.png'
    )
    await sendEditProfileForm()
  }
}

async function onFileSelected(event) {
  if (event.target.files.length) {
    const reader = new FileReader()
    let file = new File([event.target.files[0]], 'profile_picture')
    reader.onloadend = async (e) => setCroppedImg(e)
    reader.readAsDataURL(file)
  }
}

function fullProfileURL(profile_picture_url) {
  return `${imageproxiURL}/insecure/rs:fill:150:150/sm:0/plain/${profile_picture_url}@jpg`
}

function closeErrorMessage() {
  errorStore.resetErrorObject()
}

async function onLanguageChange() {
  const language = languages.value.find(
    (lang) => lang.locale === accountForm.language
  )

  if (!language.locale) return
  await changeLanguage(language.locale.split('_')[0])
}
</script>

<style lang="scss">
.EditProfileForm {
  &__link {
    width: 1.5rem;
    height: 1.5rem;
    margin-right: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__pic {
    width: rem(120);
    height: rem(120);
    background-color: var(--color-secondary);
    border-radius: 100%;
    border: 3px solid var(--color-brand-red);
    position: relative;

    &-img {
      width: 100%;
      height: 100%;
      border-radius: 100%;
    }

    &-loader {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &__edit-btn {
    display: flex;
    align-items: center;
    border-radius: 100%;
    justify-content: center;
    height: rem(32);
    width: rem(32);
    position: absolute;
    right: 0;
    bottom: 0;
    cursor: pointer;
    color: var(--color-secondary);
    background-color: var(--color-brand-purple);
    border: 2px solid var(--color-secondary);
    font-size: rem(12);
    transition: all 0.2s;

    &:hover {
      opacity: 0.9;
    }

    &__input-file {
      position: absolute;
      left: 0;
      top: 0;
      opacity: 0;
      height: rem(32);
      width: rem(32);
      cursor: pointer;
    }
  }
}
</style>
